import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { Avatar, Box, Grid, Typography, Stack, Tooltip, IconButton, LinearProgress, Chip, Fade } from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import TotalIncomeCard from 'ui-component/cards/Skeleton/TotalIncomeCard';

// assets
import BedIcon from '@mui/icons-material/Bed';
import HotelIcon from '@mui/icons-material/Hotel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BuildIcon from '@mui/icons-material/Build';

// ==============================|| STYLED COMPONENTS ||============================== //

const ProfessionalCardWrapper = styled(MainCard)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  position: 'relative',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.03)',
  transition: 'all 0.3s ease',
  minHeight: '160px',
  maxHeight: '160px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px 0 rgba(0, 0, 0, 0.08)',
    borderColor: theme.palette.primary.main
  }
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  height: 24,
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor:
    status === 'available'
      ? 'rgba(76, 175, 80, 0.1)'
      : status === 'occupied'
      ? 'rgba(211, 47, 47, 0.1)'
      : status === 'maintenance'
      ? 'rgba(158, 158, 158, 0.1)'
      : 'rgba(245, 124, 0, 0.1)',
  color: status === 'available' ? '#2e7d32' : status === 'occupied' ? '#c62828' : status === 'maintenance' ? theme.palette.text.secondary : '#e65100',
  border: 'none',
  '& .MuiChip-icon': {
    fontSize: '0.875rem',
    color: 'inherit'
  }
}));

// ==============================|| UTILITIES ||============================== //

const calculateRoomStats = (roomData) => {
  if (!roomData || roomData.length === 0) {
    return {
      total: 0,
      available: 0,
      occupied: 0,
      maintenance: 0,
      dirty: 0,
      occupancyRate: 0
    };
  }

  const stats = {
    total: roomData.length,
    available: roomData.filter((room) => room.bookingStatus !== 'active' && room.housekeepingStatus === 'clean').length,
    occupied: roomData.filter((room) => room.bookingStatus === 'active').length,
    maintenance: roomData.filter((room) => room.housekeepingStatus === 'out-of-service').length,
    dirty: roomData.filter((room) => room.housekeepingStatus === 'dirty').length
  };

  stats.occupancyRate = stats.total > 0 ? ((stats.occupied / stats.total) * 100).toFixed(1) : 0;

  return stats;
};

// ==============================|| COMPONENTE PRINCIPAL ||============================== //

const TotalRoomsCard = ({ isLoading, roomData }) => {
  const theme = useTheme();
  const [stats, setStats] = useState({
    total: 0,
    available: 0,
    occupied: 0,
    maintenance: 0,
    dirty: 0,
    occupancyRate: 0
  });
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (roomData) {
      setStats(calculateRoomStats(roomData));
    }
  }, [roomData]);

  if (isLoading) {
    return <TotalIncomeCard />;
  }

  return (
    <Fade in={true} timeout={500}>
      <ProfessionalCardWrapper border={false} content={false}>
        <Box sx={{ p: 2.5, position: 'relative', height: '100%' }}>
          {/* Sección Principal */}
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" height="100%">
            <Box flex={1}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  mb: 0.5
                }}
              >
                Total Habitaciones
              </Typography>

              <Stack direction="row" alignItems="baseline" spacing={1} mb={0.5}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: '2rem',
                    lineHeight: 1,
                    color: theme.palette.text.primary,
                    letterSpacing: '-0.5px'
                  }}
                >
                  {stats.total}
                </Typography>

                <StatusChip icon={<CheckCircleIcon />} label={`${stats.available} disp`} size="small" status="available" />
              </Stack>

              {/* Tasa de ocupación */}
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                  fontWeight: 500
                }}
              >
                Ocupación: <span style={{ color: theme.palette.text.primary, fontWeight: 600 }}>{stats.occupancyRate}%</span>
              </Typography>

              {/* Barra de estado compacta */}
              <Box sx={{ mt: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.7rem',
                    fontWeight: 500
                  }}
                >
                  Distribución de Estados
                </Typography>

                <Box sx={{ mt: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={100}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: theme.palette.grey[200],
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        background: `linear-gradient(to right, 
                          #d32f2f 0%, 
                          #d32f2f ${(stats.occupied / stats.total) * 100}%, 
                          #f57c00 ${(stats.occupied / stats.total) * 100}%, 
                          #f57c00 ${((stats.occupied + stats.dirty) / stats.total) * 100}%, 
                          #2e7d32 ${((stats.occupied + stats.dirty) / stats.total) * 100}%)`
                      }
                    }}
                  />
                </Box>

                {/* Leyenda compacta */}
                <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.65rem', fontWeight: 500 }}>
                    <span style={{ color: '#d32f2f' }}>● </span>Ocupadas ({stats.occupied})
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.65rem', fontWeight: 500 }}>
                    <span style={{ color: '#f57c00' }}>● </span>Sucias ({stats.dirty})
                  </Typography>
                </Stack>
              </Box>
            </Box>

            {/* Avatar/Icono */}
            <Avatar
              sx={{
                width: 48,
                height: 48,
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                color: '#1976d2',
                borderRadius: '12px',
                ml: 1
              }}
            >
              <HotelIcon sx={{ fontSize: 24 }} />
            </Avatar>
          </Stack>
        </Box>
      </ProfessionalCardWrapper>
    </Fade>
  );
};

TotalRoomsCard.propTypes = {
  isLoading: PropTypes.bool,
  roomData: PropTypes.array
};

export default TotalRoomsCard;
